import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, Select, Typography, message, Alert, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import request from '../../../utils/request';
import { getAllWarehouses } from '../../../services/warehouse';
import type { WarehouseDetail } from '../../../types/warehouse';

const { Title } = Typography;

const AdminUserCreate: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [warehouses, setWarehouses] = useState<WarehouseDetail[]>([]);
  const [warehousesLoading, setWarehousesLoading] = useState<boolean>(false);

  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        setWarehousesLoading(true);
        const list = await getAllWarehouses();
        setWarehouses(list || []);
      } catch (e) {
        // 已在服务内统一处理错误提示
      } finally {
        setWarehousesLoading(false);
      }
    };
    loadWarehouses();
  }, []);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const payload: any = {
        username: values.username,
        email: values.email || undefined,
        password: values.password,
        role: values.role || 'staff',
        timezone: values.timezone || 'America/New_York',
      };

      // 仅允许创建管理员或员工账号，禁止客户
      if (payload.role === 'customer') {
        message.error('此页面不支持创建客户账号');
        setSubmitting(false);
        return;
      }

      // 员工时附带 staff_sub_role
      if (payload.role === 'staff' && values.staff_sub_role) {
        payload.profile = { staff_sub_role: values.staff_sub_role };
        if (values.warehouse_id) {
          // 便于后端在创建用户时同步分配所属仓库（若后端支持）
          payload.profile.assigned_warehouse_id = values.warehouse_id;
        }
      }

      // 尝试创建系统用户
      const resp = await request.post('/api/v1/users/', payload);
      if (resp && (resp.status === 201 || resp.data?.id)) {
        message.success('系统用户创建成功');
        navigate('/dashboard/admin/users/list', { replace: true });
      } else {
        message.success('提交成功');
      }
    } catch (e: any) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.response?.data || e?.message;
      let errMsg = detail || '创建失败';
      if (status === 403) errMsg = '无权限：需要管理员权限';
      if (status === 401) errMsg = '未登录或登录已过期，请重新登录后再试';
      message.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <Title level={4} style={{ margin: 0 }}>创建系统用户</Title>
        </div>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="说明"
          description={
            <div>
              本页面用于管理员创建系统用户（管理员/员工）。如需创建“客户主账号”，请前往“客户管理-创建客户”。
            </div>
          }
        />

        <Form layout="vertical" form={form}>
          <Form.Item label="用户名" name="username" rules={[{ required: true, message: '请输入用户名' }]}>
            <Input allowClear placeholder="请输入用户名" />
          </Form.Item>

          <Form.Item label="邮箱" name="email" rules={[{ type: 'email', message: '邮箱格式不正确' }]}>
            <Input allowClear placeholder="选填：邮箱" />
          </Form.Item>

          <Form.Item label="初始密码" name="password" rules={[{ required: true, message: '请输入初始密码' }, { min: 6, message: '至少6位' }]}>
            <Input.Password placeholder="请输入初始密码" />
          </Form.Item>

          <Form.Item label="角色" name="role" initialValue="staff">
            <Select
              options={[
                { label: '员工', value: 'staff' },
                { label: '管理员', value: 'admin' },
              ]}
              placeholder="选择角色"
            />
          </Form.Item>

          {/* 员工子角色（仅当选择“员工”时显示） */}
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.role !== cur.role}>
            {({ getFieldValue }) =>
              getFieldValue('role') === 'staff' ? (
                <Form.Item label="员工子角色" name="staff_sub_role" initialValue="hq_staff">
                  <Select
                    options={[
                      { label: '总部员工', value: 'hq_staff' },
                      { label: '仓库主管', value: 'warehouse_supervisor' },
                      { label: '仓库员工', value: 'warehouse_staff' },
                    ]}
                    placeholder="选择员工子角色"
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          {/* 当选择 仓库主管/仓库员工 时显示所属仓库选择 */}
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.role !== cur.role || prev.staff_sub_role !== cur.staff_sub_role}>
            {({ getFieldValue }) => {
              const role = getFieldValue('role');
              const sub = getFieldValue('staff_sub_role');
              const needWarehouse = role === 'staff' && (sub === 'warehouse_supervisor' || sub === 'warehouse_staff');
              return needWarehouse ? (
                <Form.Item
                  label="所属仓库"
                  name="warehouse_id"
                  rules={[{ required: true, message: '请选择所属仓库' }]}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="请选择所属仓库"
                    optionFilterProp="label"
                    loading={warehousesLoading}
                    options={(warehouses || []).map((w) => ({ label: `${w.name} (${w.code})`, value: w.id }))}
                    filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
                  />
                </Form.Item>
              ) : null;
            }}
          </Form.Item>

          {/* 时区设置 */}
          <Form.Item label="时区" name="timezone" initialValue="America/New_York">
            <Select
              showSearch
              optionFilterProp="label"
              options={[
                { label: '美国东部 (America/New_York)', value: 'America/New_York' },
                { label: '美国太平洋 (America/Los_Angeles)', value: 'America/Los_Angeles' },
                { label: '中国上海 (Asia/Shanghai)', value: 'Asia/Shanghai' },
                { label: '日本东京 (Asia/Tokyo)', value: 'Asia/Tokyo' },
                { label: 'UTC (协调世界时)', value: 'UTC' },
                { label: '欧洲伦敦 (Europe/London)', value: 'Europe/London' },
              ]}
              placeholder="选择时区"
            />
          </Form.Item>

          <Space>
            <Button type="primary" loading={submitting} onClick={handleSubmit}>提交</Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default AdminUserCreate;


